import { Col, Form, FormItemProps, Input, Modal, Row } from "antd";
import { TEditRefProvider, TRefProvider } from "api/services/models/types";
import { addOrUpdateRefProvider, getRefProviderById } from "api/services/RefProviderService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import { CollapsibleGroup } from "components/CollapsibleGroup";
import { Header } from "components/common/Header";
import ErrorViewer from "components/ErrorViewer";
import { useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
// @ts-ignore
import "react-phone-number-input/style.css";

type TAddOrUpdateRefProviderProps = {
  refProvider?: TRefProvider;
  onClose: (refProvider?: TRefProvider) => void;
};

type TFormRow = {
  key: number | string;
  gutter: number;
  columns: (FormItemProps & { key: string | number })[];
};

type TFormGroup = {
  key: string | number;
  title?: string; 
  rows: TFormRow[];
};

const makeFields = () => {
  const fields: TFormGroup[] = [
    {
      key: "basic-info",
      title: "Basic Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "practiceName",
              label: "Practice Name",
              key: 1,
              hidden: false,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            },
            {
              name: "location",
              label: "Location",
              key: 2,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            }
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "rpFname",
              label: "First Name",
              key: 3,
              children: <Input className="filter-input-transparent" />,
              rules: [{ required: true, max: 50, message: "Please enter First Name" }]
            },
            {
              name: "rpMinitial",
              label: "Middle",
              key: 4,
              children: <Input maxLength={1} className="filter-input-transparent" />
            },
            {
              name: "rpLname",
              label: "Last Name",
              key: 5,
              children: <Input className="filter-input-transparent" />,
              rules: [{ required: true, max: 50, message: "Please enter Last Name" }]
            },
          ]
        },
        {
          key: "row-3",
          gutter: 8,
          columns: [
            {
              name: "credentials",
              label: "Credentials",
              key: 6,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 30 }]
            },
            {
              name: "extProviderId",
              label: "Ext. Id",
              key: 7,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            },
          ]
        },
      ]
    },
    {
      key: "address-info",
      title: "Address Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "address1",
              label: "Address 1",
              key: 8,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            },
            {
              name: "address2",
              label: "Suite",
              key: 9,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            },
            {
              name: "address3",
              label: "Address 3",
              key: 10,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }]
            },
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "city",
              label: "City",
              key: 11,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 25 }]
            },
            {
              name: "state",
              label: "State",
              key: 12,
              children: (
                <Input
                  maxLength={2}
                  pattern="[A-Za-z]{2}"
                  title="Enter exactly 2 letters"
                  className="filter-input-transparent"
                />
              )
            },
            {
              name: "zip",
              label: "Zip",
              key: 13,
              children: (
                <Input type="number" style={{ width: "100%" }} className="filter-input-transparent" />
              )
            },
          ]
        },
      ]
    },
    {
      key: "contact-info",
      title: "Contact Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "phone1",
              label: "Phone",
              key: 14,
              children: (
                <PhoneInput
                    defaultCountry="US"
                    className="PhoneInput"
                    onChange={() => { }}
                  />
              ),
              rules: [
                { required: true, message: "Phone number is required!" },
                {
                  validator: (_, value) => {
                    if (!value || isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please enter a valid phone number!"));
                  },
                },
              ]
            },
            {
              name: "fax",
              label: "Fax",
              key: 15,
              children: <Input className="filter-input-transparent" />,
              rules: [
                {
                  required: true,
                  pattern: /^\d{10}$/,
                  message: "Fax is not valid!",
                  max: 10
                }
              ]
            },
            {
              name: "email",
              label: "Email",
              key: 16,
              rules: [{ type: "email", max: 50 }],
              children: <Input className="filter-input-transparent" />
            }
          ]
        }
      ]
    }
  ]
  return fields;
};

export default function AddOrUpdateRefProvider({
  refProvider,
  onClose
}: Readonly<TAddOrUpdateRefProviderProps>) {
  const { accountId } = useTDAuthentication();
  const [localRefProvider, setLocalRefProvider] = useState<Partial<TEditRefProvider>>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();

  useEffect(() => {
    if (accountId && refProvider != null) {
      setLoading(true);
      setError(null);
      getRefProviderById(accountId, refProvider.rpId)
        .then(setLocalRefProvider)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [refProvider]);

  useEffect(() => {
    if (localRefProvider) {
      form.setFieldsValue(localRefProvider);
    }
  }, [localRefProvider]);

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(setError);
  };

  const onFinish = async (values: TEditRefProvider) => {
    const updatedRefProvider: TEditRefProvider = {
      accountId: accountId!,
      ...localRefProvider,
      ...values,
      zip: values.zip?.toString(),
      phone1: values.phone1?.toString()
    };
    setLoading(true);
    setError(null);
    addOrUpdateRefProvider(updatedRefProvider)
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      width={700}
      maskClosable={false}
      title={
        <Header
          isEditing={refProvider != null}
          header="Add Referring Provider"
          editHeader="Edit Referring Provider"
          onSubmit={onSave}
          onClose={onClose}
        />
      }
      open={true}
      footer={null}
      closeIcon={null}
      onCancel={() => onClose()}
      destroyOnClose={true}
      centered={true}>
      {error && <ErrorViewer error={error} />}
      <Form
        form={form}
        size="small"
        name="add_or_update_refproviders"
        onFinish={onFinish}
        layout="vertical"
        validateTrigger={["onChange", "onBlur"]}
        clearOnDestroy={true}
        style={{ maxHeight: "750px", overflowY: "auto" }}
        autoComplete="off">
        {makeFields().map(group => (
          <CollapsibleGroup key={group.key} title={group.title}>
            {group.rows.map(row => (
              <Row gutter={row.gutter} key={row.key}>
                {row.columns.map(col => (
                  <Col key={col.key} xs={24} sm={12} md={8} lg={8}>
                    <Form.Item
                      key={col.name}
                      name={col.name}
                      label={
                        <label htmlFor={col.name} className="input-label">
                          {col.label}
                        </label>
                      }
                      valuePropName={col.valuePropName}
                      rules={col.rules}
                      layout={col.layout}
                      style={col.style}
                      hidden={col.hidden}>
                      {col.children}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            ))}
          </CollapsibleGroup>
        ))}
      </Form>
    </Modal>
  );
}
